import { Modal, notification, Popconfirm } from 'antd';
import ApiService from 'utils/ApiCalls';

interface UseDeleteOptions {
  tableName: string;
  softDeleteFn: (id: number | string) => Promise<any>;
  onSuccess: (id: number | string) => void;
  entityLabel?: string;
  lang?: string;
}

const useDeleteWithDependencyCheck = ({
  tableName,
  softDeleteFn,
  onSuccess,
  entityLabel = 'record',
  lang = 'en'
}: UseDeleteOptions) => {
  const handleDelete = (id: number | string) => {
    Modal.confirm({
      title: 'Delete',
      content: `Are you sure you want to delete this ${entityLabel}? This action cannot be undone.`,
      okText: 'Yes',
      cancelText: 'No',
      okType: 'danger',
      onOk: () =>
        new Promise<void>((resolve) => {
          ApiService.checkDeleteDependency(tableName, id, lang)
            .then(({ data }) => {
              if (data.status === 'blocked') {
                let message =
                  data.message ||
                  `This ${entityLabel} cannot be deleted because it is being used in other records. Please remove the related references before deleting it.`;
                const tableMatch = message.match(/This record in '([^']*)'/);
                const extractedTableName = tableMatch
                  ? tableMatch[1]
                  : entityLabel;
                message = `This ${extractedTableName} cannot be deleted because it is being used in other records. Please remove the related references before deleting it.`;
                notification.error({
                  message: 'Cannot Delete',
                  description: message,
                  placement: 'topRight',
                  duration: 8
                });
                resolve();
                return;
              }
              softDeleteFn(id)
                .then(() => {
                  onSuccess(id);
                  notification.success({
                    message: 'Deleted Successfully',
                    description: `${
                      entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1)
                    } has been deleted successfully.`,
                    placement: 'topRight',
                    duration: 3
                  });
                  resolve();
                })
                .catch((error: any) => {
                  notification.error({
                    message: 'Delete Failed',
                    description:
                      error?.response?.data?.message ||
                      `Failed to delete ${entityLabel}. Please try again.`,
                    placement: 'topRight',
                    duration: 5
                  });
                  resolve();
                });
            })
            .catch((error: any) => {
              notification.error({
                message: 'Delete Failed',
                description:
                  error?.response?.data?.message ||
                  `Failed to delete ${entityLabel}. Please try again.`,
                placement: 'topRight',
                duration: 5
              });
              resolve();
            });
        })
    });
  };

  return { handleDelete };
};

export default useDeleteWithDependencyCheck;
